import axios from "axios"
import { getBaseEndpoint } from "../../../features/endpoints/endpointManager"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { RootState } from "../../../reduxStore/store"
import { practiceSlice } from "../model"
import { Practice } from "../schema"
import { cloneDeep } from "lodash"
import { DataStatus } from "../../enums"
import appInsightsLogger from "../../../features/logging/appInsightsLogger"

const getReadPracticeTagsEndpoint = (practiceId: string): string => {
  return getBaseEndpoint() + `/practice/practices/${practiceId}/tags`
}
const postHasOnlineServicesInCaliforniaEndpoint = (
  practiceId: string,
): string => {
  return (
    getBaseEndpoint() +
    `/practice/practices/${practiceId}/practiceagreement/onlineservicesincalifornia`
  )
}

const postHasNoPhysicalLocationsEndpoint = (practiceId: string): string => {
  return (
    getBaseEndpoint() +
    `/practice/practices/${practiceId}/hasNoPhysicalLocations`
  )
}

export const fetchPracticeTags = async (
  practiceId: string,
): Promise<string[]> => {
  const response = await axios.get<string[]>(
    getReadPracticeTagsEndpoint(practiceId),
  )
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error(
      `Failed to fetch practice tags with status code: ${response.status}`,
    )
  }
}

interface PostHasOfficesInCaliforniaThunkArgs {
  hasOnlineServicesInCalifornia: boolean
  practiceId: string
  setPostHasOnlineServicesInCaliforniaDataStatus?: (
    dataStatus: DataStatus,
  ) => void
}
export const postHasOnlineServicesInCalifornia = createAsyncThunk(
  "practiceAgreement/hasOnlineServicesInCalifornia",
  async (
    {
      hasOnlineServicesInCalifornia,
      practiceId,
      setPostHasOnlineServicesInCaliforniaDataStatus,
    }: PostHasOfficesInCaliforniaThunkArgs,
    { dispatch, getState },
  ) => {
    try {
      setPostHasOnlineServicesInCaliforniaDataStatus?.(DataStatus.SENDING)
      const url: string = postHasOnlineServicesInCaliforniaEndpoint(practiceId)
      const response = await axios.put(url, hasOnlineServicesInCalifornia, {
        headers: {
          "Content-Type": "application/json-patch+json",
          accept: "*/*",
        },
      })

      if (response.status === 204) {
        const state = getState() as RootState
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          hasOnlineServicesInCalifornia,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        setPostHasOnlineServicesInCaliforniaDataStatus?.(DataStatus.LOADED)
      }
    } catch (error) {
      appInsightsLogger.traceError(error)
      setPostHasOnlineServicesInCaliforniaDataStatus?.(DataStatus.ERROR)
    }
  },
)

interface PostHasNoPhysicalLocationsThunkArgs {
  hasNoPhysicalLocations: boolean
  practiceId: string
  setDataStatus?: (dataStatus: DataStatus) => void
}

export const postHasNoPhysicalLocations = createAsyncThunk(
  "practice/hasNoPhysicalLocations",
  async (
    {
      hasNoPhysicalLocations,
      practiceId,
      setDataStatus,
    }: PostHasNoPhysicalLocationsThunkArgs,
    { dispatch, getState },
  ) => {
    try {
      setDataStatus?.(DataStatus.SENDING)
      const url: string = postHasNoPhysicalLocationsEndpoint(practiceId)
      const response = await axios.put(url, hasNoPhysicalLocations, {
        headers: {
          "Content-Type": "application/json-patch+json",
          accept: "*/*",
        },
      })

      if (response.status === 204) {
        const state = getState() as RootState
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          hasNoPhysicalLocations,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        setDataStatus?.(DataStatus.LOADED)
      }
    } catch (error) {
      appInsightsLogger.traceError(error)
      setDataStatus?.(DataStatus.ERROR)
    }
  },
)
